import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Place as PlaceIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

import Map from '../../components/maps/GoogleMap';

import CategoriesCard from '../../components/exposition/CategoriesCard';
import PointsCard from '../../components/exposition/PointsCard';
import BeaconsCard from '../../components/exposition/BeaconsCard';

import api from '../../services/api';

export default function ExpositionDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  function checkAuth() {
    const isLogged = localStorage.getItem('token');
    if (!isLogged) navigate('/');
  }

  useEffect(() => {
    checkAuth();
  }, []);

  const [isLoaded, setIsLoaded] = useState(false);

  const { expositionId } = location.state;
  const [exposition, setExposition] = useState({});
  const [categories, setCategories] = useState([]);
  const [points, setPoints] = useState([]);

  const [tab, setTab] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const loadData = async () => {
    try {
      const response = await api.get(`/expositions/${expositionId}`);
      setExposition(response.data);
      setCategories(response.data.categories);
      setPoints(
        response.data.categories.reduce((acc, category) => [
          ...acc,
          ...category.points.map((point) => ({
            ...point,
            category: category.name,
          })),
        ], []),
      );
      setIsLoaded(true);
    } catch (err) {
      console.error(err);
      alert('Falha ao carregar exposição, tente recarregar a página.');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const address = `${exposition.address || ''} - ${exposition.neighborhood || ''}, ${exposition.city || ''}${exposition.state || ''}${exposition.cep || ''}`;

  const [audioFile, setAudioFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const [formData, setFormData] = useState({
    type: 'gps',
    name: '',
    category: '',
    description: '',
    radius: 10,
    beacon: '',
    isVisible: true,
    isAutomaticallyPlayed: true,
    audioUrl: '',
    imageUrl: '',
    linkURL: '',
    linkTitle: '',
  });

  const [formEditData, setFormEditData] = useState({
    id: '',
    type: 'gps',
    name: '',
    categoryId: '',
    category: '',
    description: '',
    radius: 10,
    beacon: '',
    isVisible: true,
    isAutomaticallyPlayed: true,
    audioURL: '',
    imageURL: '',
    linkURL: '',
    linkTitle: '',
  });

  const [selectedCoords, setSelectedCoords] = useState({ lat: null, lng: null });
  const [selectedPoint, setSelectedPoint] = useState(null);

  const mapRef = useRef(null);

  const scrollToMapComponent = () => {
    if (mapRef.current) {
      mapRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(exposition.url);
      setSnackbarOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const displayAddress = [
    exposition.address,
    exposition.neighborhood,
    exposition.city ? `${exposition.city}${exposition.state ? ` - ${exposition.state}` : ''}` : null,
  ].filter(Boolean).join(', ');

  const beaconPointCount = (points || []).filter((p) => p.type === 'beacon').length;
  const gpsPointCount = (points || []).filter((p) => p.type === 'gps').length;

  if (!isLoaded) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400,
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{
        maxWidth: 1600,
        margin: '0 auto',
        px: { xs: 2, md: 3 },
        py: 3,
      }}
      >
        {/* Header — voltar + título + ações */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 1, color: '#666' }}
            size="small"
          >
            Voltar
          </Button>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#2a2a2a' }}>
                {exposition.name}
              </Typography>
              {displayAddress ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                  <PlaceIcon sx={{ fontSize: 16, color: '#888' }} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {displayAddress}
                  </Typography>
                </Stack>
              ) : null}
              {exposition.url ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                  <LinkIcon sx={{ fontSize: 16, color: '#888' }} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {exposition.url}
                  </Typography>
                  <IconButton size="small" onClick={copyUrl} aria-label="Copiar URL">
                    <CopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              ) : null}
            </Box>

            {/* Ações de editar/excluir do local ficam na tela Home. */}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Chip
              label={`${categories.length} ${categories.length === 1 ? 'categoria' : 'categorias'}`}
              size="small"
              variant="outlined"
            />
            <Chip label={`${gpsPointCount} GPS`} size="small" variant="outlined" />
            <Chip label={`${beaconPointCount} beacons`} size="small" variant="outlined" />
          </Stack>
        </Box>

        {/* Mapa — largura total, centralizado */}
        <Paper
          elevation={1}
          ref={mapRef}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: '1px solid #eee',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Mapa
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Clique no mapa para adicionar um novo ponto GPS
            </Typography>
          </Stack>
          <Map
            address={address}
            categories={categories}
            points={points}
            setPoints={setPoints}
            audioFile={audioFile}
            setAudioFile={setAudioFile}
            imageFile={imageFile}
            setImageFile={setImageFile}
            formData={formData}
            setFormData={setFormData}
            formEditData={formEditData}
            setFormEditData={setFormEditData}
            selectedCoords={selectedCoords}
            setSelectedCoords={setSelectedCoords}
            selectedPoint={selectedPoint}
            setSelectedPoint={setSelectedPoint}
          />
        </Paper>

        {/* Listas em tabs — abaixo do mapa, uma tabela por vez */}
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            border: '1px solid #eee',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: '1px solid #eee',
              minHeight: 48,
              px: 2,
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontSize: 14 },
            }}
          >
            <Tab label={`Pontos GPS (${gpsPointCount})`} />
            <Tab label={`Beacons (${beaconPointCount})`} />
            <Tab label={`Categorias (${categories.length})`} />
          </Tabs>
          <Box sx={{ p: 2 }}>
            {tab === 0 && (
              <PointsCard
                scrollToMapComponent={scrollToMapComponent}
                points={points}
                setPoints={setPoints}
                setAudioFile={setAudioFile}
                setImageFile={setImageFile}
                setFormData={setFormData}
                setFormEditData={setFormEditData}
                selectedCoords={selectedCoords}
                setSelectedCoords={setSelectedCoords}
                setSelectedPoint={setSelectedPoint}
                categories={categories}
              />
            )}
            {tab === 1 && (
              <BeaconsCard
                scrollToMapComponent={scrollToMapComponent}
                points={points}
                setPoints={setPoints}
                setAudioFile={setAudioFile}
                setImageFile={setImageFile}
                setFormData={setFormData}
                setFormEditData={setFormEditData}
                selectedCoords={selectedCoords}
                setSelectedCoords={setSelectedCoords}
                setSelectedPoint={setSelectedPoint}
                categories={categories}
              />
            )}
            {tab === 2 && (
              <CategoriesCard
                expositionId={exposition.id}
                categories={categories}
              />
            )}
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="URL copiada"
      />
    </>
  );
}
